/**
 * Porta única de saída para a área de transferência.
 *
 * Por que existe (ClickFix): o padrão "botão que copia um comando" é o vetor
 * clássico do ataque — a página põe no clipboard algo diferente (ou maior) do
 * que mostra na tela, e a vítima cola no terminal / Win+R e executa. Basta um
 * `\n` no meio do texto para o shell tratar o resto como um SEGUNDO comando, e
 * a linha extra nem aparece no botão.
 *
 * Nesta extensão o texto copiado é montado a partir de dados do banco
 * (`package_name` do produto, URL do repositório, chave de licença) — dados
 * que passam por admin, por vendedor e por importação do GitHub. Nenhum deles
 * é "constante do código", então nada vai para o clipboard sem validação.
 *
 * Regra: o que é comando é validado (recusado se não casar com a forma
 * esperada), o que é valor opaco é checado como uma linha só, e o que é
 * snippet de arquivo mantém as quebras de linha mas perde caracteres de
 * controle. Recusa devolve `false` — quem chamou avisa o usuário.
 */

/**
 * Nome de pacote Composer, regex oficial do schema do composer.json.
 * Note que ela não admite espaço, `;`, `&`, `|`, `$`, aspas nem quebra de
 * linha — exatamente o que um payload de ClickFix precisa.
 */
const COMPOSER_PACKAGE = /^[a-z0-9]([_.-]?[a-z0-9]+)*\/[a-z0-9](([_.]|-{1,2})?[a-z0-9]+)*$/;

/** Caracteres de controle (incl. separadores de linha Unicode). */
const CONTROL_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F\u2028\u2029]/;

/** Encadeadores de comando / substituição do shell. */
const SHELL_METACHARS = /[;&|`$(){}<>\\'"\r\n]/;

/** Valor de uma linha só (token, chave, URL): sem controle, sem quebra. */
const ONE_LINE_UNSAFE = /[\r\n\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F\u2028\u2029]/;
export function isComposerPackage(name) {
  return typeof name === 'string' && name.length <= 120 && COMPOSER_PACKAGE.test(name);
}

/**
 * Comando `composer require <pacote>`, ou `null` se o nome do pacote não for
 * um nome de pacote de verdade. Chamadores devem esconder o botão de copiar
 * quando vier `null`: sem comando válido, não há o que colar no terminal.
 */
export function composerRequire(packageName) {
  return isComposerPackage(packageName) ? `composer require ${packageName}` : null;
}
async function write(text) {
  try {
    if (!navigator.clipboard?.writeText) return false;
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Permissão negada / contexto inseguro: o texto continua visível na tela
    // para cópia manual. Não é erro de segurança, só falha de conveniência.
    return false;
  }
}

/**
 * Comando de terminal. Recusa quebras de linha, caracteres de controle e
 * metacaracteres de shell — um comando legítimo desta extensão
 * (`composer require x/y`, `composer config ...`) não usa nenhum deles.
 */
export function copyCommand(text) {
  const value = typeof text === 'string' ? text.trim() : '';
  if (!value || value.length > 500 || SHELL_METACHARS.test(value) || CONTROL_CHARS.test(value)) {
    return Promise.resolve(false);
  }
  return write(value);
}

/**
 * Vários comandos, um por linha (o passo "registre o repositório"). Cada linha
 * passa pela mesma checagem de `copyCommand`; a quebra de linha só é aceita
 * como separador entre linhas válidas, nunca escondida dentro de um valor.
 */
export function copyCommandLines(lines) {
  const value = typeof lines === 'string' ? lines.replace(/\r\n?/g, '\n').trim() : '';
  if (!value || value.length > 2000) return Promise.resolve(false);
  const parts = value.split('\n');
  const ok = parts.every(line => {
    const l = line.trim();
    return l.length > 0 && !SHELL_METACHARS.test(l) && !CONTROL_CHARS.test(l);
  });
  return ok ? write(parts.map(l => l.trim()).join('\n')) : Promise.resolve(false);
}

/**
 * Valor opaco (chave de licença, token, URL do repositório): uma linha só, sem
 * caracteres de controle. Não é comando, então metacaractere de shell não é
 * checado — mas quebra de linha continua proibida (é o que emenda comandos).
 */
export function copyValue(text) {
  const value = typeof text === 'string' ? text.trim() : '';
  if (!value || value.length > 4000 || ONE_LINE_UNSAFE.test(value)) return Promise.resolve(false);
  return write(value);
}

/**
 * Snippet de arquivo (composer.json / auth.json): multi-linha por natureza,
 * mas vai para um EDITOR, não para o shell. Mantém `\n`, remove o resto dos
 * caracteres de controle e exige que o conteúdo seja JSON-ish já montado por
 * nós — quem chama valida as partes dinâmicas antes.
 */
export function copySnippet(text) {
  const raw = typeof text === 'string' ? text.replace(/\r\n?/g, '\n') : '';
  if (!raw.trim() || raw.length > 8000) return Promise.resolve(false);
  const value = raw.split('\n').map(line => CONTROL_CHARS.test(line) ? '' : line).join('\n');
  return value.trim() ? write(value) : Promise.resolve(false);
}
flarum.reg.add('ramon-marketplace', 'common/utils/clipboard', { isComposerPackage: isComposerPackage,composerRequire: composerRequire,copyCommand: copyCommand,copyCommandLines: copyCommandLines,copyValue: copyValue,copySnippet: copySnippet, });